import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChevronDown, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { SourceInput, type SourceInputValue } from "@/components/scenarios/SourceInput";
import type { RolePlayConfig, RolePlayScenario } from "@/lib/powtoon-be/types";
import { resolveSourceInputs } from "@/lib/scenarios/resolveSourceInputs";
import {
  isRolePlayConfigEmpty, normalizeScenario, singleScenarioOf,
} from "./schema";
import {
  generateRoleplayGoalOptions, generateRoleplayScenarios,
  enhanceRoleplayField, type RolePlayFieldKey,
} from "@/lib/scenarios/generateClient";
import { AssessmentCriteriaEditor } from "./components/AssessmentCriteriaEditor";

export function RolePlayEditor({
  value, onChange, sources, onSourcesChange, scenarioId, showErrors,
}: {
  value: RolePlayConfig;
  onChange: (next: RolePlayConfig) => void;
  sources: SourceInputValue;
  onSourcesChange: (next: SourceInputValue) => void;
  scenarioId?: string;
  showErrors?: boolean;
}) {
  const [goalOptions, setGoalOptions] = useState<string[]>([]);
  const [busyGoal, setBusyGoal] = useState(false);
  const [busyScenario, setBusyScenario] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);

  const goalSet = value.goal.trim().length > 0;
  const hasSources = sources.length > 0;
  const scenario = useMemo(() => singleScenarioOf(value), [value]);

  const setGoal = (g: string) => onChange({ ...value, goal: g });
  // Always exactly one scenario, and its id is preserved across every edit —
  // the Powtoon BE resolves the scoring rubric by that id.
  const patchScenario = (patch: Partial<RolePlayScenario>) =>
    onChange({ ...value, scenarios: [{ ...scenario, ...patch }] });

  async function handleGenerateGoalOptions() {
    setBusyGoal(true);
    try {
      const inputs = await resolveSourceInputs(sources, scenarioId);
      if (inputs.length === 0) throw new Error("Add at least one source with content");
      const { options, warnings } = await generateRoleplayGoalOptions({ inputs });
      if (options.length === 0) throw new Error("No goals returned");
      setGoalOptions(options);
      warnings.forEach((w) => toast.warning(`${w.file_name ?? "Source"}: ${w.error}`));
      toast.success("Goal options generated");
    } catch (e) {
      toast.error(`Generation failed: ${(e as Error).message}`);
    } finally { setBusyGoal(false); }
  }

  async function runGenerateScenario() {
    setBusyScenario(true);
    try {
      const inputs = await resolveSourceInputs(sources, scenarioId);
      if (inputs.length === 0) throw new Error("Add at least one source with content");
      const { scenarios: next, warnings } = await generateRoleplayScenarios({
        inputs, goal: value.goal, count: 1,
      });
      if (next.length === 0) throw new Error("No scenario returned");
      // Keep the existing id so past sessions and transcripts still resolve.
      const fresh = normalizeScenario({ ...next[0], id: scenario.id });
      onChange({ ...value, scenarios: [fresh] });
      warnings.forEach((w) => toast.warning(`${w.file_name ?? "Source"}: ${w.error}`));
      toast.success("Scenario generated");
    } catch (e) {
      toast.error(`Generation failed: ${(e as Error).message}`);
    } finally { setBusyScenario(false); }
  }

  function handleGenerateClick() {
    if (!isRolePlayConfigEmpty({ ...value, goal: "" })) {
      setConfirmReplace(true);
      return;
    }
    void runGenerateScenario();
  }

  return (
    <div className="space-y-5">
      {/* Section A — Sources (re-uses SourceInput; its generic Generate button is hidden) */}
      <SourceInput
        mode="role_play"
        value={sources}
        onChange={onSourcesChange}
        scenarioId={scenarioId}
        onGenerated={() => { /* role_play has its own generation flows */ }}
        hideGenerate
      />

      {/* Section B — Goal */}
      <section className="space-y-3 rounded-lg border bg-card p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold">Training goal</h3>
            <p className="text-sm text-muted-foreground">
              What should the learner achieve in this role play? Pick a suggestion or write your own.
            </p>
          </div>
          <Button
            type="button" variant="secondary" size="sm"
            onClick={() => void handleGenerateGoalOptions()}
            disabled={!hasSources || busyGoal}
          >
            {busyGoal ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
            {goalOptions.length > 0 ? "Regenerate options" : "Generate 3 options"}
          </Button>
        </div>

        {goalOptions.length > 0 && (
          <div className="grid gap-2 sm:grid-cols-3">
            {goalOptions.map((opt, i) => {
              const selected = opt === value.goal;
              return (
                <Card
                  key={i}
                  className={cn(
                    "cursor-pointer p-3 text-sm transition-colors",
                    selected ? "border-primary ring-1 ring-primary bg-primary/5" : "hover:border-primary/50",
                  )}
                  onClick={() => setGoal(opt)}
                >
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                    Option {i + 1}
                  </div>
                  {opt}
                </Card>
              );
            })}
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="rp-goal">Goal</Label>
          <Textarea
            id="rp-goal"
            value={value.goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="e.g. Practise handling an upset client who feels unheard."
            className={cn(
              "min-h-[80px]",
              showErrors && !goalSet && "border-destructive focus-visible:ring-destructive",
            )}
          />
          {showErrors && !goalSet && (
            <p className="text-xs text-destructive">Training goal is required</p>
          )}
        </div>
      </section>

      {/* Section C — The scenario (exactly one) */}
      <section className="space-y-3 rounded-lg border bg-card p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold">Scenario</h3>
            <p className="text-sm text-muted-foreground">
              The situation the learner plays through. Generate it from your sources or write it yourself.
            </p>
          </div>
          <Button
            type="button" size="sm"
            onClick={handleGenerateClick}
            disabled={!goalSet || !hasSources || busyScenario}
          >
            {busyScenario
              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              : <Sparkles className="h-3.5 w-3.5 mr-1" />}
            Generate scenario
          </Button>
        </div>

        <ScenarioFields
          scenario={scenario}
          onChange={patchScenario}
          showErrors={showErrors}
          knowledgeForEnhance={value.goal}
        />
      </section>

      <AlertDialog open={confirmReplace} onOpenChange={setConfirmReplace}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace the scenario?</AlertDialogTitle>
            <AlertDialogDescription>
              This overwrites everything you wrote in the scenario below with freshly generated content, based on the goal and sources.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setConfirmReplace(false); void runGenerateScenario(); }}
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ScenarioFields({
  scenario, onChange, showErrors, knowledgeForEnhance,
}: {
  scenario: RolePlayScenario;
  onChange: (patch: Partial<RolePlayScenario>) => void;
  showErrors?: boolean;
  knowledgeForEnhance: string;
}) {
  const [enhancing, setEnhancing] = useState<RolePlayFieldKey | null>(null);

  async function enhance(field: RolePlayFieldKey, current: string, setter: (v: string) => void) {
    setEnhancing(field);
    try {
      const ctx = JSON.stringify({
        avatar_role: scenario.avatar_role,
        learner_role: scenario.learner_role,
        emotional_state: scenario.emotional_state,
      });
      const out = await enhanceRoleplayField({
        field, currentValue: current, scenarioContext: ctx, knowledge: knowledgeForEnhance,
      });
      setter(out);
      toast.success("Enhanced");
    } catch (e) {
      toast.error(`Enhance failed: ${(e as Error).message}`);
    } finally { setEnhancing(null); }
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Avatar role *" error={showErrors && !scenario.avatar_role.trim() ? "Required" : undefined}>
          <Input
            value={scenario.avatar_role}
            onChange={(e) => onChange({ avatar_role: e.target.value })}
            placeholder="e.g. Frustrated customer"
          />
        </Field>
        <Field label="Learner role *" error={showErrors && !scenario.learner_role.trim() ? "Required" : undefined}>
          <Input
            value={scenario.learner_role}
            onChange={(e) => onChange({ learner_role: e.target.value })}
            placeholder="e.g. Support agent"
          />
        </Field>
      </div>

      <Field label="Emotional state / mood">
        <Input
          value={scenario.emotional_state}
          onChange={(e) => onChange({ emotional_state: e.target.value })}
          placeholder="e.g. anxious, irritated, hopeful"
        />
      </Field>

      <EnhanceableTextarea
        label="Avatar personality"
        value={scenario.avatar_personality}
        onChange={(v) => onChange({ avatar_personality: v })}
        placeholder="Backstory, tone, mannerisms…"
        minRows={3}
        enhancing={enhancing === "roleplay_avatar_personality"}
        onEnhance={() => enhance("roleplay_avatar_personality", scenario.avatar_personality,
          (v) => onChange({ avatar_personality: v }))}
      />

      <EnhanceableTextarea
        label="Hidden information"
        hint="Facts the avatar won't reveal unless the learner draws them out."
        value={scenario.hidden_information}
        onChange={(v) => onChange({ hidden_information: v })}
        minRows={3}
        enhancing={enhancing === "roleplay_hidden_information"}
        onEnhance={() => enhance("roleplay_hidden_information", scenario.hidden_information,
          (v) => onChange({ hidden_information: v }))}
      />

      <EnhanceableTextarea
        label="Emotional progression rules"
        hint="If the learner does X, the avatar's response becomes Y."
        value={scenario.emotional_progression}
        onChange={(v) => onChange({ emotional_progression: v })}
        minRows={4}
        enhancing={enhancing === "roleplay_emotional_progression"}
        onEnhance={() => enhance("roleplay_emotional_progression", scenario.emotional_progression,
          (v) => onChange({ emotional_progression: v }))}
      />

      <Collapsible>
        <CollapsibleTrigger asChild>
          <Button type="button" variant="ghost" size="sm" className="px-1 text-xs">
            Framing script (optional) <ChevronDown className="h-3 w-3 ml-1" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <EnhanceableTextarea
            label=""
            value={scenario.framing_script ?? ""}
            onChange={(v) => onChange({ framing_script: v })}
            placeholder="Optional opening narration the avatar uses to set the scene."
            minRows={3}
            enhancing={enhancing === "roleplay_framing_script"}
            onEnhance={() => enhance("roleplay_framing_script", scenario.framing_script ?? "",
              (v) => onChange({ framing_script: v }))}
          />
        </CollapsibleContent>
      </Collapsible>

      <AssessmentCriteriaEditor
        value={scenario.assessment}
        onChange={(a) => onChange({ assessment: a })}
        terminationRequired={showErrors}
      />
    </div>
  );
}

function Field({
  label, error, children,
}: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      {label && <Label className="text-xs">{label}</Label>}
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function EnhanceableTextarea({
  label, hint, value, onChange, placeholder, minRows, enhancing, onEnhance,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  minRows: number;
  enhancing: boolean;
  onEnhance: () => void;
}) {
  const minH = `${minRows * 24 + 16}px`;
  return (
    <div className="space-y-1">
      {label && <Label className="text-xs">{label}</Label>}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      <div className="relative">
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={enhancing}
          style={{ minHeight: minH }}
          className="pb-9 text-sm"
        />
        <Button
          type="button" variant="secondary" size="sm"
          onClick={onEnhance} disabled={enhancing}
          className="absolute bottom-1.5 right-1.5 h-7 px-2 text-xs"
        >
          {enhancing
            ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            : <Sparkles className="h-3 w-3 mr-1" />}
          {value.trim() ? "Enhance" : "Generate"}
        </Button>
      </div>
    </div>
  );
}
